import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Home, Search, Compass, ChevronLeft, Map } from "lucide-react";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: Path not found:",
      location.pathname
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7] px-4 overflow-hidden relative">
      {/* Decorative Background Elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-amber-100/40 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-100/40 rounded-full blur-[120px]" />

      <div className="max-w-2xl w-full relative z-10 text-center">
        {/* Animated Icon Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center mb-8"
        >
          <div className="relative">
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 text-slate-100"
            >
              <Compass size={160} strokeWidth={0.5} />
            </motion.div>
            <div className="relative bg-white p-8 rounded-[2.5rem] shadow-2xl shadow-slate-200 border border-slate-50">
              <Map className="w-16 h-16 text-amber-600" />
            </div>
          </div>
        </motion.div>

        {/* Text Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h1 className="text-8xl md:text-9xl font-serif font-bold text-slate-900/10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[140%] select-none">
            404
          </h1>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-4 relative z-10">
            Seeking the Way?
          </h2>
          <p className="text-slate-500 font-serif italic text-lg mb-12 max-w-md mx-auto leading-relaxed">
            "The page you are looking for has been moved or no longer exists. Let us help you find your way back to the fold."
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/"
            className="group flex items-center gap-3 bg-slate-900 text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-[11px] transition-all hover:bg-slate-800 hover:shadow-xl hover:-translate-y-1"
          >
            <Home size={16} className="text-amber-500" />
            Back to Home
          </Link>

          <button
            onClick={() => window.history.back()}
            className="group flex items-center gap-3 bg-white text-slate-600 border border-slate-200 px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-[11px] transition-all hover:bg-slate-50"
          >
            <ChevronLeft size={16} />
            Previous Page
          </button>
        </motion.div>

        {/* Helpful Links Grid */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-16 pt-12 border-t border-slate-100 grid grid-cols-2 md:grid-cols-3 gap-8"
        >
          <div className="text-center">
            <Link to="/about/history" className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-amber-600 transition-colors">Our History</Link>
          </div>
          <div className="text-center border-x border-slate-100 px-4">
            <Link to="/news" className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-amber-600 transition-colors">Latest News</Link>
          </div>
          <div className="text-center col-span-2 md:col-span-1">
            <Link to="/contact" className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-amber-600 transition-colors">Contact Us</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound;